export const SITE_URL = "https://staql.com";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/og/staql-og.png`;
export const DEFAULT_OG_ALT = "STAQL — Technologie die voor je werkt. Data, automatisering en IoT voor het MKB.";

export type SeoEntry = {
  path: string;
  title: string;
  description: string;
  label: string;
  noindex?: boolean;
  priority?: number;
  changefreq?: "weekly" | "monthly" | "yearly";
  breadcrumbs?: Array<{ name: string; path: string }>;
  serviceType?: string;
};

export const seoEntries: SeoEntry[] = [
  {
    path: "/",
    title: "Data, automatisering & IoT in het Gooi en Almere | STAQL",
    description: "STAQL helpt MKB in Hilversum, Blaricum, Naarden, Huizen en Almere met data, AI, automatisering, websoftware en IoT. Eén technisch aanspreekpunt.",
    label: "Home",
    priority: 1,
    changefreq: "weekly"
  },
  {
    path: "/diensten",
    title: "ICT-diensten voor MKB in het Gooi en Almere | STAQL",
    description: "ICT-diensten voor MKB in Hilversum, Blaricum, Naarden, Huizen en Almere. Van data en AI tot automatisering, software en slimme systemen.",
    label: "Diensten",
    priority: .9,
    changefreq: "monthly",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Diensten", path: "/diensten" }]
  },
  {
    path: "/diensten/data-ai",
    title: "Data & AI voor MKB in het Gooi en Almere | STAQL",
    description: "Data, dashboards, BI en praktische AI voor MKB in Hilversum, Blaricum, Naarden, Huizen en Almere. Van infrastructuur tot bruikbaar inzicht.",
    label: "Data & AI",
    priority: .9,
    changefreq: "monthly",
    serviceType: "Data, business intelligence en AI",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Diensten", path: "/diensten" }, { name: "Data & AI", path: "/diensten/data-ai" }]
  },
  {
    path: "/diensten/automatisering-software",
    title: "Automatisering & software in het Gooi en Almere | STAQL",
    description: "Procesautomatisering, integraties en websoftware voor MKB in Hilversum, Blaricum, Naarden, Huizen en Almere. Minder handwerk en meer grip.",
    label: "Automatisering & software",
    priority: .9,
    changefreq: "monthly",
    serviceType: "Procesautomatisering, integraties en maatwerksoftware",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Diensten", path: "/diensten" }, { name: "Automatisering & software", path: "/diensten/automatisering-software" }]
  },
  {
    path: "/diensten/iot-slimme-systemen",
    title: "IoT & slimme systemen in het Gooi en Almere | STAQL",
    description: "IoT, monitoring, energiedata en slimme systemen voor MKB in Hilversum, Blaricum, Naarden, Huizen en Almere. Van pilot tot werkende oplossing.",
    label: "IoT & slimme systemen",
    priority: .9,
    changefreq: "monthly",
    serviceType: "IoT, monitoring en slimme systemen",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Diensten", path: "/diensten" }, { name: "IoT & slimme systemen", path: "/diensten/iot-slimme-systemen" }]
  },
  {
    path: "/cases",
    title: "Data- en automatiseringscases voor het MKB | STAQL",
    description: "Bekijk hoe STAQL bedrijven helpt met data, dashboards, automatisering, software en IoT. Praktische oplossingen en zichtbaar resultaat.",
    label: "Cases",
    priority: .75,
    changefreq: "monthly",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Cases", path: "/cases" }]
  },
  {
    path: "/cases/schenk-recycling",
    title: "Afvaldata inzichtelijk voor Schenk Recycling | STAQL",
    description: "STAQL gaf klanten van Schenk Recycling volledig inzicht in afvalgegevens met dashboards die duurzaamheids- en CSRD-rapportage ondersteunen.",
    label: "Schenk Recycling",
    priority: .8,
    changefreq: "yearly",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Cases", path: "/cases" }, { name: "Schenk Recycling", path: "/cases/schenk-recycling" }]
  },
  {
    path: "/over-staql",
    title: "Over STAQL | ICT-partner in het Gooi en Almere",
    description: "Maak kennis met STAQL en oprichter Youssef. Nuchtere expertise in data, automatisering, software en IoT voor MKB in het Gooi en Almere.",
    label: "Over STAQL",
    priority: .7,
    changefreq: "yearly",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Over STAQL", path: "/over-staql" }]
  },
  {
    path: "/contact",
    title: "Contact met STAQL in het Gooi en Almere",
    description: "Bespreek je ICT-vraag met STAQL voor Hilversum, Blaricum, Naarden, Huizen en Almere. Gebruik het formulier of bel 035 369 01 50.",
    label: "Contact",
    priority: .8,
    changefreq: "yearly",
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]
  },
  {
    path: "/privacy",
    title: "Privacyverklaring | STAQL",
    description: "Lees hoe STAQL omgaat met persoonsgegevens en contactaanvragen.",
    label: "Privacy",
    noindex: true,
    breadcrumbs: [{ name: "Home", path: "/" }, { name: "Privacy", path: "/privacy" }]
  },
  {
    path: "/404",
    title: "Pagina niet gevonden | STAQL",
    description: "Deze pagina bestaat niet of is verplaatst.",
    label: "Niet gevonden",
    noindex: true
  }
];

export function normalizePath(pathname: string) {
  const path = pathname.split(/[?#]/)[0].replace(/\/+$/, "") || "/";
  return path;
}

export function getSeoEntry(pathname: string) {
  const path = normalizePath(pathname);
  return seoEntries.find((entry) => entry.path === path) ?? seoEntries.find((entry) => entry.path === "/404")!;
}

export function canonicalUrl(pathname: string) {
  const path = normalizePath(pathname);
  return path === "/" ? `${SITE_URL}/` : `${SITE_URL}${path}`;
}

export function structuredDataFor(pathname: string) {
  const entry = getSeoEntry(pathname);
  const canonical = canonicalUrl(entry.path);
  const serviceAreas = [
    { "@type": "AdministrativeArea", name: "Het Gooi" },
    ...["Hilversum", "Blaricum", "Naarden", "Huizen", "Almere"].map((name) => ({ "@type": "City", name }))
  ];
  const organization = {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "STAQL",
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/media/staql-logo.png`,
    telephone: "+31 35 369 01 50",
    address: { "@type": "PostalAddress", streetAddress: "Botterstraat 47", postalCode: "1271 XL", addressLocality: "Huizen", addressRegion: "Noord-Holland", addressCountry: "NL" },
    areaServed: serviceAreas
  };

  const graph: Array<Record<string, unknown>> = [organization];
  if (entry.path === "/") {
    graph.unshift({
      "@type": "WebSite",
      "@id": `${SITE_URL}/#website`,
      url: `${SITE_URL}/`,
      name: "STAQL",
      inLanguage: "nl-NL",
      publisher: { "@id": `${SITE_URL}/#organization` }
    });
  } else {
    graph.unshift({
      "@type": "WebPage",
      "@id": `${canonical}#webpage`,
      url: canonical,
      name: entry.title,
      description: entry.description,
      inLanguage: "nl-NL",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` }
    });
  }

  if (entry.breadcrumbs) {
    graph.push({
      "@type": "BreadcrumbList",
      itemListElement: entry.breadcrumbs.map((crumb, index) => ({
        "@type": "ListItem",
        position: index + 1,
        name: crumb.name,
        item: canonicalUrl(crumb.path)
      }))
    });
  }

  if (entry.serviceType) {
    graph.push({
      "@type": "Service",
      name: entry.serviceType,
      description: entry.description,
      url: canonical,
      provider: { "@id": `${SITE_URL}/#organization` },
      areaServed: serviceAreas,
      audience: { "@type": "BusinessAudience", audienceType: "MKB en kleine bedrijven" }
    });
  }

  return { "@context": "https://schema.org", "@graph": graph };
}
